import { supabase } from '../lib/supabase'

export const performanceService = {
  // Save performance analysis
  async savePerformanceAnalysis(courseId, analysisType, data, comments = '', preparedBy = '', verifiedBy = '') {
    try {
      console.log(`Saving ${analysisType} analysis with comments:`, comments);
      const { data: result, error } = await supabase
        .from('performance_analysis_clomapper')
        .upsert({
          course_id: courseId,
          analysis_type: analysisType,
          data: data,
          comments: comments,
          prepared_by: preparedBy,
          verified_by: verifiedBy
        })
        .select();
      if (error) throw error;
      return { data: result, error: null };
    } catch (error) {
      return { data: null, error: error.message };
    }
  },

  // Get performance analysis
  async getPerformanceAnalysis(courseId, analysisType) {
    try {
      const { data, error } = await supabase
        .from('performance_analysis_clomapper')
        .select('*')
        .eq('course_id', courseId)
        .eq('analysis_type', analysisType)
        .single();
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error.message };
    }
  },

  // Get all performance analysis for a course
  async getAllPerformanceAnalysis(courseId) {
    try {
      const { data, error } = await supabase
        .from('performance_analysis_clomapper')
        .select('*')
        .eq('course_id', courseId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      return { data: null, error: error.message };
    }
  },

  // NEW: Save comments and suggestions - FIXED VERSION
  async saveCommentsAndSuggestions(courseId, sectionType, comments, suggestions) {
    try {
      console.log(`Saving ${sectionType} comments and suggestions`);
      console.log("Comments:", comments);
      console.log("Suggestions:", suggestions);
      
      // First check if a record exists
      const { data: existingRecord, error: checkError } = await supabase
        .from('comments_suggestions_clomapper')
        .select('id')
        .eq('course_id', courseId)
        .eq('section_type', sectionType)
        .maybeSingle();
      
      let result;
      
      if (existingRecord) {
        // Update existing record
        result = await supabase
          .from('comments_suggestions_clomapper')
          .update({
            comments: comments,
            suggestions: suggestions,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingRecord.id)
          .select();
      } else {
        // Insert new record
        result = await supabase
          .from('comments_suggestions_clomapper')
          .insert({
            course_id: courseId,
            section_type: sectionType,
            comments: comments,
            suggestions: suggestions
          })
          .select();
      }
      
      if (result.error) throw result.error;
      
      return { data: result.data?.[0], error: null };
    } catch (error) {
      console.error('Error saving comments and suggestions:', error);
      return { data: null, error: error.message };
    }
  },

  // NEW: Get comments and suggestions
  async getCommentsAndSuggestions(courseId, sectionType) {
    try {
      const { data, error } = await supabase
        .from('comments_suggestions_clomapper')
        .select('*')
        .eq('course_id', courseId)
        .eq('section_type', sectionType)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error; // PGRST116 is "no rows returned" error
      
      return { data, error: null };
    } catch (error) {
      console.error('Error getting comments and suggestions:', error);
      return { data: null, error: error.message };
    }
  },

  // Calculate comprehensive course statistics
  async calculateCourseStatistics(courseId) {
    try {
      // Get all related data in parallel for efficiency
      const [
        studentsResult,
        assessmentsResult,
        cloStatementsResult,
        mappingResult
      ] = await Promise.all([
        supabase
          .from('students_clomapper')
          .select(`
            *,
            student_marks_clomapper (
              assessment_id,
              marks
            )
          `)
          .eq('course_id', courseId),
        supabase
          .from('assessments_clomapper')
          .select('*')
          .eq('course_id', courseId),
        supabase
          .from('clo_statements_clomapper')
          .select('*')
          .eq('course_id', courseId),
        supabase
          .from('clo_plo_mapping_clomapper')
          .select('*')
          .eq('course_id', courseId)
      ]);
      
      if (studentsResult.error) throw studentsResult.error;
      if (assessmentsResult.error) throw assessmentsResult.error;
      if (cloStatementsResult.error) throw cloStatementsResult.error;
      if (mappingResult.error) throw mappingResult.error;
      
      // Process and calculate statistics
      const statistics = this.processStatistics(
        studentsResult.data,
        assessmentsResult.data,
        cloStatementsResult.data,
        mappingResult.data
      );
      
      return { data: statistics, error: null };
    } catch (error) {
      return { data: null, error: error.message };
    }
  },

  // Process statistics (helper method)
  processStatistics(students, assessments, cloStatements, mapping) {
    const stats = {
      totalStudents: students.length,
      totalAssessments: assessments.length,
      gradeDistribution: [],
      cloAchievement: [],
      ploAchievement: [],
      passFailStats: { pass: 0, fail: 0 }
    };

    // Calculate grade distribution
    const gradeRanges = [
      { grade: 'A+', min: 90, max: 100, count: 0 },
      { grade: 'A', min: 80, max: 89, count: 0 },
      { grade: 'A-', min: 75, max: 79, count: 0 },
      { grade: 'B+', min: 70, max: 74, count: 0 },
      { grade: 'B', min: 65, max: 69, count: 0 },
      { grade: 'B-', min: 60, max: 64, count: 0 },
      { grade: 'C+', min: 55, max: 59, count: 0 },
      { grade: 'C', min: 50, max: 54, count: 0 },
      { grade: 'C-', min: 45, max: 49, count: 0 },
      { grade: 'D', min: 40, max: 44, count: 0 },
      { grade: 'F', min: 0, max: 39, count: 0 }
    ];

    students.forEach(student => {
      const totalMarks = student.student_marks_clomapper.reduce((sum, mark) => sum + mark.marks, 0);
      const gradeRange = gradeRanges.find(range => totalMarks >= range.min && totalMarks <= range.max);
      
      if (gradeRange) {
        gradeRange.count++;
      }
      
      if (totalMarks >= 40) {
        stats.passFailStats.pass++;
      } else {
        stats.passFailStats.fail++;
      }
    });

    stats.gradeDistribution = gradeRanges;

    // Calculate CLO achievement (simplified)
    stats.cloAchievement = cloStatements.map(clo => ({
      clo: clo.clo_code,
      achieved: Math.floor(Math.random() * 40) + 60, // Mock data
      notAchieved: Math.floor(Math.random() * 40) + 10
    }));

    // Calculate PLO achievement (simplified)
    const uniquePLOs = [...new Set(mapping.map(m => m.plo_code))];
    stats.ploAchievement = uniquePLOs.map(plo => ({
      plo: plo,
      achieved: Math.floor(Math.random() * 40) + 60, // Mock data
      notAchieved: Math.floor(Math.random() * 40) + 10
    }));

    return stats;
  },

  // New: Generate comprehensive course report with class group breakdown
  async generateComprehensiveReport(courseId, options = {}) {
    try {
      const { includeClassGroups = true, selectedGroups = ['All Groups'] } = options;
      
      // Get all students with their marks
      const { data: students, error: studentsError } = await supabase
        .from('students_clomapper')
        .select(`
          *,
          student_marks_clomapper (
            assessment_id,
            marks
          )
        `)
        .eq('course_id', courseId);
      
      if (studentsError) throw studentsError;
      
      // Get all assessments - FIXED: Changed assessments_order to assessment_order
      const { data: assessments, error: assessmentsError } = await supabase
        .from('assessments_clomapper')
        .select('*')
        .eq('course_id', courseId)
        .order('id', { ascending: true });
      
      if (assessmentsError) throw assessmentsError;
      
      // Process data
      const report = {
        overallReport: this.generateOverallReport(students, assessments),
        groupReports: {}
      };
      
      // Generate class group reports if requested
      if (includeClassGroups) {
        // Group students by class group
        const studentsByGroup = {};
        
        students.forEach(student => {
          const group = student.class_group || student.classGroup || 'Ungrouped';
          if (!studentsByGroup[group]) {
            studentsByGroup[group] = [];
          }
          studentsByGroup[group].push(student);
        });
        
        // Generate report for each requested group
        Object.keys(studentsByGroup).forEach(group => {
          if (selectedGroups.includes('All Groups') || selectedGroups.includes(group)) {
            report.groupReports[group] = this.generateOverallReport(
              studentsByGroup[group], 
              assessments,
              group
            );
          }
        });
      }
      
      return { data: report, error: null };
    } catch (error) {
      console.error('Error generating comprehensive report:', error);
      return { data: null, error: error.message };
    }
  },
  
  // Helper method to generate overall report
  generateOverallReport(students, assessments, groupName = 'All Students') {
    // Grade distribution calculation
    const gradeRanges = [
      { grade: 'A+', min: 90, max: 100, gpa: 4.000, count: 0 },
      { grade: 'A', min: 80, max: 89, gpa: 4.000, count: 0 },
      { grade: 'A-', min: 75, max: 79, gpa: 3.700, count: 0 },
      { grade: 'B+', min: 70, max: 74, gpa: 3.300, count: 0 },
      { grade: 'B', min: 65, max: 69, gpa: 3.000, count: 0 },
      { grade: 'B-', min: 60, max: 64, gpa: 2.700, count: 0 },
      { grade: 'C+', min: 55, max: 59, gpa: 2.300, count: 0 },
      { grade: 'C', min: 50, max: 54, gpa: 2.000, count: 0 },
      { grade: 'C-', min: 45, max: 49, gpa: 1.700, count: 0 },
      { grade: 'D', min: 40, max: 44, gpa: 1.000, count: 0 },
      { grade: 'F', min: 0, max: 39, gpa: 0.000, count: 0 }
    ];
    
    // Student detailed marks
    const studentDetails = [];
    let passCount = 0;
    let failCount = 0;
    let highestMark = 0;
    let lowestMark = 100;
    let totalMarks = 0;
    
    // Assessment averages
    const assessmentAverages = assessments.reduce((acc, assessment) => {
      acc[assessment.id] = { 
        total: 0, 
        count: 0, 
        average: 0,
        name: assessment.assessment_name || assessment.name,
        maxMarks: assessment.marks
      };
      return acc;
    }, {});
    
    // Process each student
    students.forEach(student => {
      // Calculate total marks for this student
      let studentTotalMarks = 0;
      const marksByAssessment = {};
      
      // Process marks for each assessment
      assessments.forEach(assessment => {
        const mark = student.student_marks_clomapper.find(m => m.assessment_id === assessment.id);
        const markValue = mark ? mark.marks : 0;
        
        marksByAssessment[assessment.id] = markValue;
        studentTotalMarks += markValue;
        
        // Update assessment averages
        assessmentAverages[assessment.id].total += markValue;
        assessmentAverages[assessment.id].count++;
      });
      
      // Update statistics
      totalMarks += studentTotalMarks;
      highestMark = Math.max(highestMark, studentTotalMarks);
      lowestMark = Math.min(lowestMark, studentTotalMarks);
      
      // Determine grade
      const gradeRange = gradeRanges.find(range => 
        studentTotalMarks >= range.min && studentTotalMarks <= range.max
      );
      
      if (gradeRange) {
        gradeRange.count++;
      }
      
      // Pass/fail count
      if (studentTotalMarks >= 40) {
        passCount++;
      } else {
        failCount++;
      }
      
      // Add to student details
      studentDetails.push({
        id: student.id,
        name: student.name,
        matricId: student.matric_id,
        classGroup: student.class_group || student.classGroup || 'Ungrouped',
        faculty: student.faculty || '',
        totalMarks: studentTotalMarks,
        grade: gradeRange ? gradeRange.grade : 'N/A',
        marksByAssessment
      });
    });
    
    // Calculate assessment averages
    Object.keys(assessmentAverages).forEach(id => {
      const assessment = assessmentAverages[id];
      assessment.average = assessment.count > 0 
        ? (assessment.total / assessment.count).toFixed(2) 
        : 0;
    });
    
    // Calculate class average
    const classAverage = students.length > 0 
      ? (totalMarks / students.length).toFixed(2) 
      : 0;
    
    return {
      groupName,
      totalStudents: students.length,
      passCount,
      failCount,
      passRate: students.length > 0 ? ((passCount / students.length) * 100).toFixed(2) : 0,
      highestMark,
      lowestMark,
      classAverage,
      gradeDistribution: gradeRanges,
      assessmentAverages,
      studentDetails: studentDetails.sort((a, b) => b.totalMarks - a.totalMarks)
    };
  }
};

export default performanceService;